'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import Mailcheck from 'mailcheck';

interface EmailTypoSuggestionProps {
  email: string;
  onAccept: (suggested: string) => void;
}

interface MailcheckSuggestion {
  full: string;
  address?: string;
  domain?: string;
}

/**
 * Email typo suggestion using the mailcheck package. Detects common
 * email-domain typos (e.g. "gmial.com" → "gmail.com") and offers a
 * one-click correction.
 *
 * Renders nothing when no suggestion is available.
 *
 * Implementation: mailcheck.run is synchronous (its `suggested`/`empty`
 * callbacks fire immediately), so the suggestion is computed via useMemo
 * rather than useEffect+useState. This avoids the cascading-render
 * anti-pattern of calling setState directly in an effect.
 */
export function EmailTypoSuggestion({
  email,
  onAccept,
}: EmailTypoSuggestionProps) {
  const t = useTranslations('auth');
  const suggestion = useMemo<string | null>(() => {
    if (!email || !email.includes('@')) {
      return null;
    }
    let result: string | null = null;
    Mailcheck.run({
      email,
      suggested: (s: MailcheckSuggestion) => {
        if (s && s.full && s.full !== email) {
          result = s.full;
        }
      },
      empty: () => {
        result = null;
      },
    });
    return result;
  }, [email]);

  if (!suggestion) return null;

  return (
    <p className="text-xs text-gray-600">
      {t.rich('emailTypo.didYouMean', {
        domain: suggestion,
        suggest: (chunks) => (
          <button
            type="button"
            onClick={() => onAccept(suggestion)}
            className="font-medium text-[#2fa5fb] underline hover:text-[#1f8de0]"
          >
            {chunks}
          </button>
        ),
      })}
    </p>
  );
}
